import React from 'react';
import { AlertTriangle, Database } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { FOREST_GREEN, MID_GRAY, BORDER } from '@/styles/tokens';

interface RemoveDataConfirmModalProps {
  isOpen: boolean;
  componentName: string;
  dataSourceLabel: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export const RemoveDataConfirmModal: React.FC<RemoveDataConfirmModalProps> = ({
  isOpen,
  componentName,
  dataSourceLabel,
  onConfirm,
  onCancel,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent variant="light" className="sm:max-w-md p-0 gap-0">
        <div className="px-6 pt-6 pb-4">
          {/* Icon + heading */}
          <div className="flex items-start gap-3 mb-4">
            <div
              className="flex-shrink-0 flex items-center justify-center rounded-full"
              style={{
                width: 40,
                height: 40,
                backgroundColor: 'rgba(239, 68, 68, 0.08)',
              }}
            >
              <AlertTriangle className="h-5 w-5 text-red-500" />
            </div>
            <div>
              <h3
                className="text-base font-semibold"
                style={{ color: '#1F2937' }}
              >
                Remove Data Connection
              </h3>
              <p
                className="text-sm mt-1"
                style={{ color: MID_GRAY.DEFAULT, lineHeight: 1.5 }}
              >
                This will disconnect the data source from{' '}
                <strong style={{ color: '#374151' }}>{componentName}</strong>.
                The component will revert to showing no live data.
              </p>
            </div>
          </div>

          {/* Data source info card */}
          {dataSourceLabel && (
            <div
              className="rounded-lg flex items-center gap-2.5 px-3 py-2.5 mb-1"
              style={{
                backgroundColor: '#F9FAFB',
                border: `1px solid ${BORDER.DEFAULT}`,
              }}
            >
              <Database className="h-4 w-4 flex-shrink-0" style={{ color: MID_GRAY.DEFAULT }} />
              <span className="text-sm truncate" style={{ color: '#374151' }}>
                {dataSourceLabel}
              </span>
            </div>
          )}
        </div>

        {/* Footer buttons */}
        <div
          className="flex justify-end gap-2.5 px-6 py-4"
          style={{
            borderTop: `1px solid ${BORDER.DEFAULT}`,
            backgroundColor: '#F9FAFB',
          }}
        >
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
            style={{
              color: '#374151',
              backgroundColor: 'white',
              border: `1px solid ${BORDER.DEFAULT}`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#F3F4F6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors text-white"
            style={{
              backgroundColor: '#EF4444',
              border: '1px solid #EF4444',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#DC2626';
              e.currentTarget.style.borderColor = '#DC2626';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#EF4444';
              e.currentTarget.style.borderColor = '#EF4444';
            }}
          >
            Remove Data
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
